import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useIsMobile } from '../../hooks/useIsMobile';
import { MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH } from '../../hooks/useColumnWidths';
import './DataTable.css';

const SELECTION_COLUMN_WIDTH = 50;

export default function DataTable({ 
  columns, 
  data, 
  loading, 
  sortConfig, 
  onSort, 
  onRowClick, 
  onRowRightClick, // Add right-click handler
  emptyMessage = 'No data found',
  filteredMessage = 'No items match your filters',

  // Virtualization (optional)
  virtualize = false,
  virtualizeEstimateRowHeight = 44,
  virtualizeOverscan = 10,
  
  // Multi-select functionality
  selectedRows = new Set(),
  onSelectionChange,
  selectableRowKey = 'id', // Key to use for row identification
  
  // Optional expanded rows functionality
  expandedRows = null, // Set of expanded row IDs
  expandedRowsData = {}, // Object mapping row ID to array of expanded data
  renderExpandedRow = null, // Function to render expanded content: (parentRow, expandedData) => JSX
  
  // Custom row classes and styles
  getRowClassName = null, // Function to get custom class for a row: (row) => string
  getRowStyle = null, // Function to get custom styles for a row: (row) => object
  
  // Hierarchical child toggle support
  onChildToggle = null,

  // Column resizing (optional). Widths are keyed by column key and only contain
  // the columns the user actually dragged; the rest size automatically.
  enableColumnResize = false,
  columnWidths = null,
  onColumnResize = null
}) {
  const isMobile = useIsMobile();

  const tableOffsetRef = useRef(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  const resizable = Boolean(enableColumnResize && onColumnResize && !isMobile);

  // Live width while dragging, so the drag feels immediate without pushing a
  // state update (and a view save) through the parent on every mouse move.
  const [draftResize, setDraftResize] = useState(null); // { key, width }
  const draftResizeRef = useRef(null);
  const headerRefs = useRef(new Map());
  // Set while a drag is in flight so the mouseup doesn't also trigger a sort.
  const didResizeRef = useRef(false);

  const setHeaderRef = useCallback((columnKey, element) => {
    if (element) {
      headerRefs.current.set(columnKey, element);
    } else {
      headerRefs.current.delete(columnKey);
    }
  }, []);

  const getColumnWidth = useCallback((column) => {
    if (draftResize && draftResize.key === column.key) return draftResize.width;
    const stored = columnWidths ? columnWidths[column.key] : null;
    if (Number.isFinite(stored)) return stored;
    return column.width || null;
  }, [columnWidths, draftResize]);

  const handleResizeStart = useCallback((event, columnKey) => {
    if (!resizable) return;
    // Only respond to the primary button; let right-click fall through.
    if (event.button != null && event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();

    const headerEl = headerRefs.current.get(columnKey);
    const measuredWidth = headerEl ? headerEl.getBoundingClientRect().width : MIN_COLUMN_WIDTH;
    const startWidth = Math.round(
      Number.isFinite(columnWidths?.[columnKey]) ? columnWidths[columnKey] : measuredWidth
    );
    const startX = event.clientX;

    didResizeRef.current = false;
    draftResizeRef.current = { key: columnKey, width: startWidth };
    setDraftResize({ key: columnKey, width: startWidth });
    document.body.classList.add('is-resizing-column');

    const handleMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      if (Math.abs(delta) > 2) didResizeRef.current = true;

      const width = Math.round(
        Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, startWidth + delta))
      );
      draftResizeRef.current = { key: columnKey, width };
      setDraftResize({ key: columnKey, width });
    };

    const handleEnd = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
      document.body.classList.remove('is-resizing-column');

      const finalWidth = draftResizeRef.current?.width;
      const moved = didResizeRef.current;
      draftResizeRef.current = null;
      setDraftResize(null);

      if (moved && Number.isFinite(finalWidth)) {
        onColumnResize(columnKey, finalWidth);
      }

      // The click event fires immediately after pointerup, so clear the sort
      // guard on the next tick rather than leaving it set for the next click.
      setTimeout(() => {
        didResizeRef.current = false;
      }, 0);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);
  }, [resizable, columnWidths, onColumnResize]);

  // Double-clicking the handle drops the override and returns the column to its
  // automatic width.
  const handleResizeReset = useCallback((event, columnKey) => {
    if (!resizable) return;
    event.preventDefault();
    event.stopPropagation();
    didResizeRef.current = true;
    onColumnResize(columnKey, null);
    setTimeout(() => {
      didResizeRef.current = false;
    }, 0);
  }, [resizable, onColumnResize]);

  useEffect(() => () => {
    document.body.classList.remove('is-resizing-column');
  }, []);

  useLayoutEffect(() => {
    if (!virtualize || isMobile) return;

    const measure = () => {
      const el = tableOffsetRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const theadEl = el.querySelector('thead');
      const theadHeight = theadEl ? theadEl.getBoundingClientRect().height : 0;

      // Distance from top of document to the start of the virtualized rows.
      setScrollMargin(rect.top + window.scrollY + theadHeight);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [virtualize, isMobile, columns?.length, data?.length]);

  const orderedSelectableIds = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    const ids = [];

    for (const row of data) {
      const rowId = row?.[selectableRowKey];
      if (rowId != null) ids.push(rowId);

      const ctx = row?._hierarchyContext || {};
      const childrenData = row?.children || [];
      if (ctx.hasChildren && ctx.isExpanded && Array.isArray(childrenData) && childrenData.length > 0) {
        for (const childRow of childrenData) {
          const childId = childRow?.[selectableRowKey];
          if (childId != null) ids.push(childId);
        }
      }
    }

    return ids;
  }, [data, selectableRowKey]);

  const idToIndex = useMemo(() => {
    const map = new Map();
    orderedSelectableIds.forEach((id, index) => map.set(id, index));
    return map;
  }, [orderedSelectableIds]);

  const lastSelectedIndexRef = useRef(null);

  const sortableColumns = Array.isArray(columns)
    ? columns.filter((col) => col && col.sortable !== false)
    : [];

  const currentSortLabel = sortableColumns.find((col) => col.key === sortConfig?.key)?.label;

  const handleSort = (key) => {
    // Swallow the click that ends a column drag.
    if (didResizeRef.current) {
      didResizeRef.current = false;
      return;
    }

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    onSort({ key, direction });
  };

  const handleMobileSortKeyChange = (event) => {
    if (!onSort) return;
    const key = event.target.value;
    if (!key) return;
    onSort({ key, direction: sortConfig?.direction || 'asc' });
  };

  const handleMobileSortDirectionToggle = () => {
    if (!onSort) return;
    if (!sortConfig?.key) return;
    onSort({
      key: sortConfig.key,
      direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <i className="bi bi-arrow-down-up sort-icon inactive"></i>;
    }
    return sortConfig.direction === 'asc' 
      ? <i className="bi bi-arrow-up sort-icon active"></i>
      : <i className="bi bi-arrow-down sort-icon active"></i>;
  };

  const handleRowAuxClick = useCallback((event, row) => {
    // Middle-click should behave like opening a link in a new tab.
    if (!onRowClick) return;
    if (event?.button !== 1) return;
    onRowClick(event, row);
  }, [onRowClick]);

  // Multi-select handlers
  const handleSelectAll = (checked) => {
    if (!onSelectionChange) return;

    lastSelectedIndexRef.current = null;
    
    if (checked) {
      const allIds = new Set(data.map(row => row[selectableRowKey]));
      onSelectionChange(allIds);
    } else {
      onSelectionChange(new Set());
    }
  };

  const handleSelectRow = (rowId, checked, event) => {
    if (!onSelectionChange) return;

    const currentIndex = idToIndex.get(rowId);
    const isShift = Boolean(event?.shiftKey);
    
    const newSelection = new Set(selectedRows);

    if (isShift && Number.isFinite(currentIndex) && Number.isFinite(lastSelectedIndexRef.current)) {
      const start = Math.min(lastSelectedIndexRef.current, currentIndex);
      const end = Math.max(lastSelectedIndexRef.current, currentIndex);

      for (let i = start; i <= end; i++) {
        const idInRange = orderedSelectableIds[i];
        if (idInRange == null) continue;
        if (checked) {
          newSelection.add(idInRange);
        } else {
          newSelection.delete(idInRange);
        }
      }
    } else {
    if (checked) {
      newSelection.add(rowId);
    } else {
      newSelection.delete(rowId);
    }
    }

    if (Number.isFinite(currentIndex)) {
      lastSelectedIndexRef.current = currentIndex;
    }
    onSelectionChange(newSelection);
  };

  const syncHoverWithParentRow = (event, isEntering) => {
    const toggleRow = event.currentTarget;
    const parentRow = toggleRow.previousElementSibling;
    if (!parentRow || !parentRow.classList?.contains('table-row')) {
      return;
    }

    if (isEntering) {
      parentRow.classList.add('linked-hover');
    } else {
      parentRow.classList.remove('linked-hover');
    }
  };

  const isAllSelected = selectedRows && selectedRows.size > 0 && selectedRows.size === data.length;
  const isSomeSelected = selectedRows && selectedRows.size > 0 && selectedRows.size < data.length;

  const colSpan = columns.length + (onSelectionChange ? 1 : 0);

  const renderRows = useMemo(() => {
    if (!virtualize || isMobile) return null;

    const rows = [];

    for (let index = 0; index < data.length; index++) {
      const row = data[index];
      if (!row) continue;

      const rowId = row[selectableRowKey];
      const rowExpandedData = (expandedRowsData && expandedRowsData[row.id]) || [];
      const hierarchyContext = row._hierarchyContext || {};
      const childrenData = row.children || [];
      const hasChildToggle = Boolean(
        typeof onChildToggle === 'function' &&
          hierarchyContext &&
          hierarchyContext.hasChildren &&
          !hierarchyContext.isChild
      );

      rows.push({
        key: `row-${row.id ?? rowId ?? index}`,
        type: 'main',
        row
      });

      if (expandedRows && renderExpandedRow && rowExpandedData.length > 0) {
        rows.push({
          key: `expanded-${row.id ?? rowId ?? index}`,
          type: 'expanded',
          row,
          expandedData: rowExpandedData
        });
      }

      if (hierarchyContext.hasChildren && hierarchyContext.isExpanded && Array.isArray(childrenData)) {
        for (const childRow of childrenData) {
          if (!childRow) continue;
          const childId = childRow[selectableRowKey];
          rows.push({
            key: `child-${row.id ?? rowId ?? index}-${childRow.id ?? childId ?? ''}`,
            type: 'child',
            row: childRow,
            parentRow: row
          });
        }
      }

      if (hasChildToggle) {
        rows.push({
          key: `toggle-${row.id ?? rowId ?? index}`,
          type: 'toggle',
          row
        });
      }
    }

    return rows;
  }, [
    virtualize,
    isMobile,
    data,
    selectableRowKey,
    expandedRows,
    expandedRowsData,
    renderExpandedRow,
    onChildToggle
  ]);

  const rowVirtualizer = useWindowVirtualizer({
    count: virtualize && !isMobile && renderRows ? renderRows.length : 0,
    estimateSize: () => virtualizeEstimateRowHeight,
    overscan: virtualizeOverscan,
    scrollMargin
  });

  const virtualItems = virtualize && !isMobile && renderRows ? rowVirtualizer.getVirtualItems() : null;
  const paddingTop =
    virtualItems && virtualItems.length > 0
      ? Math.max(0, virtualItems[0].start - scrollMargin)
      : 0;
  const paddingBottom =
    virtualItems && virtualItems.length > 0
      ? Math.max(0, rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1].end - scrollMargin))
      : 0;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <i className="bi bi-arrow-clockwise"></i>
        </div>
        <p>Loading...</p>
      </div>
    );
  }

  // Mobile-first: render as a card list so users can actually see the data
  // without relying on horizontal table scrolling.
  if (isMobile) {
    return (
      <div className="table-container table-container--mobile">
        {(onSort && sortableColumns.length > 0) && (
          <div className="data-cards-toolbar">
            <div className="data-cards-toolbar-left">
              <span className="data-cards-toolbar-label">Sort</span>
              <select
                className="form-select form-select-sm data-cards-sort-select"
                value={sortConfig?.key || ''}
                onChange={handleMobileSortKeyChange}
                aria-label="Sort by"
              >
                <option value="" disabled>
                  Select field
                </option>
                {sortableColumns.map((col) => (
                  <option key={col.key} value={col.key}>
                    {col.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm data-cards-sort-direction"
              onClick={handleMobileSortDirectionToggle}
              disabled={!sortConfig?.key}
              aria-label="Toggle sort direction"
              title={currentSortLabel ? `Sort ${sortConfig?.direction === 'asc' ? 'ascending' : 'descending'} by ${currentSortLabel}` : undefined}
            >
              <i className={`bi ${sortConfig?.direction === 'asc' ? 'bi-sort-up' : 'bi-sort-down'}`}></i>
            </button>
          </div>
        )}

        {data.length === 0 ? (
          <div className="no-data no-data--cards">{emptyMessage}</div>
        ) : (
          <div className="data-cards">
            {data.map((row, index) => {
              const rowId = row?.[selectableRowKey];
              const hierarchyContext = row?._hierarchyContext || {};
              const hasChildren = Boolean(hierarchyContext?.hasChildren);
              const isChild = Boolean(hierarchyContext?.isChild);
              const indentLevel = hierarchyContext?.indentLevel || (isChild ? 1 : 0);
              const childrenData = row?.children || [];

              const renderCard = (cardRow, cardContext = cardRow?._hierarchyContext || {}, keyPrefix = '') => {
                const cardRowId = cardRow?.[selectableRowKey];
                const cardSelected = selectedRows && selectedRows.has(cardRowId);
                const cardIndent = cardContext?.indentLevel || 0;
                const cardIsChild = Boolean(cardContext?.isChild);

                return (
                  <div
                    key={`${keyPrefix}${cardRow?.id || cardRowId || index}`}
                    className={`data-card ${cardSelected ? 'selected' : ''} ${getRowClassName ? getRowClassName(cardRow) : ''} ${cardIsChild ? 'data-card--child' : ''}`}
                    style={{
                      ...(getRowStyle ? getRowStyle(cardRow) : {}),
                      marginLeft: cardIndent ? `${Math.min(2, cardIndent) * 12}px` : undefined
                    }}
                    onClick={onRowClick ? (e) => onRowClick(e, cardRow) : undefined}
                    onAuxClick={onRowClick ? (e) => handleRowAuxClick(e, cardRow) : undefined}
                    onContextMenu={onRowRightClick ? (e) => onRowRightClick(e, cardRow) : undefined}
                    data-status={cardRow?.status || ''}
                  >
                    <div className="data-card-header">
                      {onSelectionChange && (
                        <div
                          className="checkbox-container"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectRow(cardRowId, !(selectedRows && selectedRows.has(cardRowId)), e);
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={Boolean(selectedRows && selectedRows.has(cardRowId))}
                            readOnly
                            className="circular-checkbox"
                            style={{ accentColor: 'var(--nsi-brand)' }}
                          />
                        </div>
                      )}

                      <div className="data-card-title" style={{ minWidth: 0 }}>
                        {/* Prefer first column as the title */}
                        {columns?.[0]?.render
                          ? columns[0].render(cardRow, cardContext)
                          : (cardRow?.[columns?.[0]?.key] || '-')}
                      </div>
                    </div>

                    <div className="data-card-fields">
                      {columns.slice(1).map((column) => (
                        <div key={column.key} className="data-card-field">
                          <div className="data-card-field-label">{column.label}</div>
                          <div className={`data-card-field-value ${column.className || ''}`}>
                            {column.render ? column.render(cardRow, cardContext) : (cardRow?.[column.key] || '-')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              };

              return (
                <React.Fragment key={row?.id || rowId || index}>
                  {renderCard(row, { ...hierarchyContext, indentLevel }, '')}

                  {/* Render children if expanded */}
                  {hasChildren && hierarchyContext?.isExpanded && childrenData.map((childRow) => (
                    renderCard(
                      childRow,
                      {
                        isChild: true,
                        parentId: row?.id,
                        indentLevel: 1,
                        hasChildren: false,
                        isExpanded: false,
                        childCount: 0,
                        isHiddenByParent: false
                      },
                      `child-`
                    )
                  ))}

                  {/* Child toggle bar */}
                  {typeof onChildToggle === 'function' && hasChildren && !isChild && (
                    <button
                      type="button"
                      className={`child-toggle-bar ${hierarchyContext?.isExpanded ? 'expanded' : ''} child-toggle-bar--mobile`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onChildToggle(e, row?.id);
                      }}
                    >
                      <i className="bi bi-caret-down-fill toggle-icon"></i>
                      <span>{hierarchyContext?.isExpanded ? 'Collapse children' : 'Expand children'}</span>
                    </button>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const renderVirtualRow = (virtualRow) => {
    const item = renderRows[virtualRow.index];
    if (!item) return null;

    if (item.type === 'main') {
      const row = item.row;
      const rowId = row[selectableRowKey];
      const isSelected = selectedRows && selectedRows.has(rowId);
      return (
        <tr
          key={item.key}
          data-index={virtualRow.index}
          ref={rowVirtualizer.measureElement}
          className={`table-row ${isSelected ? 'selected' : ''} ${getRowClassName ? getRowClassName(row) : ''}`}
          onClick={onRowClick ? (e) => onRowClick(e, row) : undefined}
          onAuxClick={onRowClick ? (e) => handleRowAuxClick(e, row) : undefined}
          onContextMenu={onRowRightClick ? (e) => onRowRightClick(e, row) : undefined}
          style={{
            cursor: onRowClick ? 'pointer' : 'default',
            backgroundColor: isSelected ? 'rgba(var(--nsi-brand-rgb), 0.1)' : '',
            ...(getRowStyle ? getRowStyle(row) : {})
          }}
          data-status={row.status || ''}
          data-popup-trigger="true"
        >
          {onSelectionChange && (
            <td className="selection-column" style={{ width: '50px' }}>
              <div
                className="checkbox-container"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectRow(rowId, !isSelected, e);
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  className="circular-checkbox"
                  style={{
                    accentColor: 'var(--nsi-brand)'
                  }}
                />
              </div>
            </td>
          )}
          {columns.map((column) => (
            <td key={column.key} className={column.className || ''}>
              {column.render ? column.render(row, row._hierarchyContext) : row[column.key] || '-'}
            </td>
          ))}
        </tr>
      );
    }

    if (item.type === 'expanded') {
      const row = item.row;
      const isExpanded = expandedRows && expandedRows.has(row.id);
      return (
        <tr
          key={item.key}
          data-index={virtualRow.index}
          ref={rowVirtualizer.measureElement}
          className={`expanded-row ${isExpanded ? 'open' : 'closed'}`}
        >
          <td colSpan={colSpan} className={`expanded-cell ${isExpanded ? 'open' : 'closed'}`}>
            {renderExpandedRow(row, item.expandedData, isExpanded)}
          </td>
        </tr>
      );
    }

    if (item.type === 'child') {
      const childRow = item.row;
      const parentRow = item.parentRow;
      const childId = childRow[selectableRowKey];
      const isSelected = selectedRows && selectedRows.has(childId);

      return (
        <tr
          key={item.key}
          data-index={virtualRow.index}
          ref={rowVirtualizer.measureElement}
          className={`table-row child-item-row ${isSelected ? 'selected' : ''} ${getRowClassName ? getRowClassName(childRow) : ''}`}
          onClick={onRowClick ? (e) => onRowClick(e, childRow) : undefined}
          onAuxClick={onRowClick ? (e) => handleRowAuxClick(e, childRow) : undefined}
          onContextMenu={onRowRightClick ? (e) => onRowRightClick(e, childRow) : undefined}
          style={{
            cursor: onRowClick ? 'pointer' : 'default',
            backgroundColor: isSelected ? 'rgba(var(--nsi-brand-rgb), 0.1)' : '',
            ...(getRowStyle ? getRowStyle(childRow) : {})
          }}
          data-status={childRow.status || ''}
          data-popup-trigger="true"
        >
          {onSelectionChange && (
            <td className="selection-column" style={{ width: '50px' }}>
              <div
                className="checkbox-container"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectRow(childId, !isSelected, e);
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  className="circular-checkbox"
                  style={{
                    accentColor: 'var(--nsi-brand)'
                  }}
                />
              </div>
            </td>
          )}
          {columns.map((column) => (
            <td key={column.key} className={column.className || ''}>
              {column.render
                ? column.render(childRow, {
                    isChild: true,
                    parentId: parentRow.id,
                    indentLevel: 1,
                    hasChildren: false,
                    isExpanded: false,
                    childCount: 0,
                    isHiddenByParent: false
                  })
                : childRow[column.key] || '-'}
            </td>
          ))}
        </tr>
      );
    }

    if (item.type === 'toggle') {
      const row = item.row;
      const hierarchyContext = row._hierarchyContext || {};
      return (
        <tr
          key={item.key}
          data-index={virtualRow.index}
          ref={rowVirtualizer.measureElement}
          className={`child-toggle-row ${hierarchyContext.isExpanded ? 'expanded' : 'collapsed'}`}
          onMouseEnter={(e) => syncHoverWithParentRow(e, true)}
          onMouseLeave={(e) => syncHoverWithParentRow(e, false)}
        >
          <td colSpan={colSpan}>
            <button
              type="button"
              className={`child-toggle-bar ${hierarchyContext.isExpanded ? 'expanded' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof onChildToggle === 'function') {
                  onChildToggle(e, row.id);
                }
              }}
            >
              <i className="bi bi-caret-down-fill toggle-icon"></i>
              <span>{hierarchyContext.isExpanded ? 'Collapse children' : 'Expand children'}</span>
            </button>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <div ref={tableOffsetRef} className={`table-container ${isMobile ? 'table-container--mobile' : ''}`}>
      <div className={`table-scroll ${isMobile ? 'table-scroll--mobile' : ''}`}>
        <table className={`data-table ${resizable ? 'resizable-columns' : ''}`}>
        {/* table-layout is fixed, so these <col> widths drive the whole table.
            Columns without an explicit width share the remaining space. */}
        <colgroup>
          {onSelectionChange && <col style={{ width: `${SELECTION_COLUMN_WIDTH}px` }} />}
          {columns.map((column) => {
            const width = getColumnWidth(column);
            return (
              <col
                key={column.key}
                style={width ? { width: typeof width === 'number' ? `${width}px` : width } : undefined}
              />
            );
          })}
        </colgroup>
        <thead>
          <tr>
            {onSelectionChange && (
              <th className="selection-column" style={{ width: '50px' }}>
                <div 
                  className="checkbox-container"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAll(!isAllSelected);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={checkboxRef => {
                      if (checkboxRef) checkboxRef.indeterminate = isSomeSelected && !isAllSelected;
                    }}
                    readOnly
                    className="circular-checkbox"
                    style={{
                      accentColor: 'var(--nsi-brand)'
                    }}
                  />
                </div>
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                ref={(element) => setHeaderRef(column.key, element)}
                className={`${column.sortable !== false ? 'sortable' : ''} ${column.className || ''} ${draftResize?.key === column.key ? 'is-resizing' : ''}`}
                onClick={column.sortable !== false ? () => handleSort(column.key) : undefined}
              >
                <div className="th-content">
                  <span className="th-label">{column.label}</span>
                  {column.sortable !== false && getSortIcon(column.key)}
                </div>
                {resizable && (
                  <span
                    className={`column-resizer ${draftResize?.key === column.key ? 'active' : ''}`}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Resize ${column.label} column`}
                    title="Drag to resize - double-click to reset"
                    onPointerDown={(e) => handleResizeStart(e, column.key)}
                    onDoubleClick={(e) => handleResizeReset(e, column.key)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="no-data">
                {emptyMessage}
              </td>
            </tr>
          ) : virtualize && !isMobile && renderRows ? (
            <>
              {paddingTop > 0 && (
                <tr aria-hidden="true">
                  <td colSpan={colSpan} style={{ height: `${paddingTop}px`, padding: 0, border: 'none' }} />
                </tr>
              )}

              {virtualItems.map((virtualRow) => renderVirtualRow(virtualRow))}

              {paddingBottom > 0 && (
                <tr aria-hidden="true">
                  <td colSpan={colSpan} style={{ height: `${paddingBottom}px`, padding: 0, border: 'none' }} />
                </tr>
              )}
            </>
          ) : (
            data.map((row, index) => {
              const isExpanded = expandedRows && expandedRows.has(row.id);
              const rowExpandedData = (expandedRowsData && expandedRowsData[row.id]) || [];
              const rowId = row[selectableRowKey];
              const isSelected = selectedRows && selectedRows.has(rowId);
              const hierarchyContext = row._hierarchyContext || {};
              const hasChildToggle = Boolean(
                typeof onChildToggle === 'function' &&
                hierarchyContext &&
                hierarchyContext.hasChildren &&
                !hierarchyContext.isChild
              );

              // Get child data for this parent row from the original hierarchical data passed to table
              const childrenData = row.children || [];
              
              return (
                <React.Fragment key={row.id || index}>
                  {/* Main Row */}
                  <tr 
                    className={`table-row ${isSelected ? 'selected' : ''} ${getRowClassName ? getRowClassName(row) : ''}`}
                    onClick={onRowClick ? (e) => onRowClick(e, row) : undefined}
                    onAuxClick={onRowClick ? (e) => handleRowAuxClick(e, row) : undefined}
                    onContextMenu={onRowRightClick ? (e) => onRowRightClick(e, row) : undefined}
                    style={{ 
                      cursor: onRowClick ? 'pointer' : 'default',
                      backgroundColor: isSelected ? 'rgba(var(--nsi-brand-rgb), 0.1)' : '',
                      ...(getRowStyle ? getRowStyle(row) : {})
                    }}
                    data-status={row.status || ''}
                    data-popup-trigger="true"
                  >
                    {onSelectionChange && (
                      <td className="selection-column" style={{ width: '50px' }}>
                        <div 
                          className="checkbox-container"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectRow(rowId, !isSelected, e);
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="circular-checkbox"
                            style={{
                              accentColor: 'var(--nsi-brand)'
                            }}
                          />
                        </div>
                      </td>
                    )}
                    {columns.map((column) => (
                      <td key={column.key} className={column.className || ''}>
                        {column.render ? column.render(row, row._hierarchyContext) : (row[column.key] || '-')}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Expanded Row (if expanded rows functionality is enabled and row has expanded data) */}
                  {expandedRows && renderExpandedRow && rowExpandedData.length > 0 && (
                    <tr className={`expanded-row ${isExpanded ? 'open' : 'closed'}`}>
                      <td colSpan={colSpan} className={`expanded-cell ${isExpanded ? 'open' : 'closed'}`}>
                        {renderExpandedRow(row, rowExpandedData, isExpanded)}
                      </td>
                    </tr>
                  )}

                  {/* Render children if expanded */}
                  {hierarchyContext.hasChildren && hierarchyContext.isExpanded && childrenData.map(childRow => (
                    <tr 
                      key={childRow.id}
                      className={`table-row child-item-row ${selectedRows && selectedRows.has(childRow[selectableRowKey]) ? 'selected' : ''} ${getRowClassName ? getRowClassName(childRow) : ''}`}
                      onClick={onRowClick ? (e) => onRowClick(e, childRow) : undefined}
                      onAuxClick={onRowClick ? (e) => handleRowAuxClick(e, childRow) : undefined}
                      onContextMenu={onRowRightClick ? (e) => onRowRightClick(e, childRow) : undefined}
                      style={{ 
                        cursor: onRowClick ? 'pointer' : 'default',
                        backgroundColor: selectedRows && selectedRows.has(childRow[selectableRowKey]) ? 'rgba(var(--nsi-brand-rgb), 0.1)' : '',
                        ...(getRowStyle ? getRowStyle(childRow) : {})
                      }}
                      data-status={childRow.status || ''}
                      data-popup-trigger="true"
                    >
                      {onSelectionChange && (
                        <td className="selection-column" style={{ width: '50px' }}>
                          <div 
                            className="checkbox-container"
                            onClick={(e) => {
                              e.stopPropagation();
                                handleSelectRow(childRow[selectableRowKey], !selectedRows.has(childRow[selectableRowKey]), e);
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedRows && selectedRows.has(childRow[selectableRowKey])}
                              readOnly
                              className="circular-checkbox"
                              style={{
                                accentColor: 'var(--nsi-brand)'
                              }}
                            />
                          </div>
                        </td>
                      )}
                      {columns.map((column) => (
                        <td key={column.key} className={column.className || ''}>
                          {column.render ? column.render(childRow, { 
                            isChild: true, 
                            parentId: row.id, 
                            indentLevel: 1,
                            hasChildren: false,
                            isExpanded: false,
                            childCount: 0,
                            isHiddenByParent: false
                          }) : (childRow[column.key] || '-')}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Child toggle row - placed after children when expanded */}
                  {hasChildToggle && (
                    <tr
                      className={`child-toggle-row ${hierarchyContext.isExpanded ? 'expanded' : 'collapsed'}`}
                      onMouseEnter={(e) => syncHoverWithParentRow(e, true)}
                      onMouseLeave={(e) => syncHoverWithParentRow(e, false)}
                    >
                      <td colSpan={colSpan}>
                        <button
                          type="button"
                          className={`child-toggle-bar ${hierarchyContext.isExpanded ? 'expanded' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (typeof onChildToggle === 'function') {
                              onChildToggle(e, row.id);
                            }
                          }}
                        >
                          <i className="bi bi-caret-down-fill toggle-icon"></i>
                          <span>{hierarchyContext.isExpanded ? 'Collapse children' : 'Expand children'}</span>
                        </button>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
        </table>
      </div>
    </div>
  );
}